import { enableErrorTracking, ErrorTrackingPlugin, trackError } from '@snowplow/browser-plugin-error-tracking'
import { flushBuffer, newTracker, trackPageView, trackSelfDescribingEvent } from '@snowplow/browser-tracker'

import { CommonStepType } from '../types/types'

export interface SnowPlowData {
  [key: string]: string | boolean
}

export const NotAvailable = 'N/A'
type NotAvailable = typeof NotAvailable

// AnalyticsFields is just a 'string' type but this allows 'N/A' to be autocompleted
type AnalyticsField = (string & {}) | NotAvailable

export interface AnalyticsData extends SnowPlowData {
  src: AnalyticsField | typeof NotAvailable
  flow: AnalyticsField
  product: AnalyticsField | typeof NotAvailable
  version: AnalyticsField
  ui_lang: AnalyticsField
}

export type CustomAnalyticsEvent = { category: string; name: string }

export enum CommonAnalyticsPageViewEvent {
  Authenticate = 'Authenticate',
  GetUser = 'GetUser',
  Initialize = 'Initialize',
  AuthenticationCancelledByUser = 'AuthenticationCancelledByUser',
  Send = 'Send',
}

export type CommonAnalyticsCustomEventName =
  | 'OkButton'
  | 'CancelButton'
  | 'ExitButton'
  | 'ConfirmButton'
  | 'ContinueButton'
  | 'BackButton'
  | 'SaveButton'
  | 'LogoutButton'
  | 'AbortButton'
  | 'RejectButton'
  | 'AutoLogoutTimeout'
  | 'RetryButton'
  | CommonAnalyticsModalName

export type CommonAnalyticsModalName =
  | 'CloseModal'
  | 'TimeoutWarningModal'
  | 'DiscardChangesModal'
  | 'ConfirmDataDependencyUpdateModal'
  | 'ErrorModal'
  | 'ThankYouModal'
  | 'AfterCareModal'
  | 'ModalQuit'
  | 'ModalQuitEdit'

export type CommonAnalyticsServiceType = AnalyticsService<
  string,
  CommonAnalyticsCustomEventName,
  CommonAnalyticsModalName
>

enum SnowplowSchema {
  Common = 'iglu:fi.sok/hakemukset_common/jsonschema/1-0-0',
  CustomAction = 'iglu:fi.sok/hakemukset_custom_action/jsonschema/1-0-0',
}

export const ANALYTICS_PRODUCT_NOT_APPLICABLE = 'NotApplicable'

export abstract class AnalyticsService<T_STEPTYPE, T_CUSTOMEVENTNAME, T_MODALNAME extends CommonAnalyticsModalName> {
  public abstract sendPageViewEvent(step: T_STEPTYPE): void

  public abstract sendCustomEvent(step: T_STEPTYPE | T_MODALNAME, name: T_CUSTOMEVENTNAME): void

  public abstract sendCustomDataEvent(data: { [key: string]: string }): void

  public abstract sendModalViewEvent(modal: CommonAnalyticsModalName | T_MODALNAME): void

  public flushEvents(): void {
    flushBuffer({})
  }
}

export function initializeSnowplow(
  appId: string = import.meta.env.VITE_SNOWPLOW_APP_ID,
  collectorEndpoint: string = import.meta.env.VITE_SNOWPLOW_COLLECTOR_ENDPOINT,
): void {
  if (appId && collectorEndpoint) {
    newTracker('sp1', collectorEndpoint, {
      appId,
      eventMethod: 'post',
      anonymousTracking: { withServerAnonymisation: true, withSessionTracking: false },
      stateStorageStrategy: 'none',
      contexts: { webPage: true },
      encodeBase64: true,
      plugins: [ErrorTrackingPlugin()],
    })
    enableErrorTracking({
      contextAdder: (error) => {
        if (error instanceof ErrorEvent) {
          return [
            {
              schema: SnowplowSchema.Common,
              data: {
                fileName: error?.filename,
              },
            },
          ]
        } else return []
      },
    })
  }
}

export const sendPageViewEventToAnalytics = (name: string, data: AnalyticsData): void => {
  trackPageView({
    title: resolveAnalyticsPage(name),
    context: [
      {
        schema: SnowplowSchema.Common,
        data,
      },
    ],
  })
}

export const sendErrorEventToAnalytics = (message: string, error?: Error): void => {
  trackError({ message, error })
}

export const sendEndpointRetryEventToAnalytics = (
  retryCount: number,
  method?: string,
  url?: string,
  error?: Error,
): void => {
  sendErrorEventToAnalytics(
    `Endpoint call (${method}) ${url} failed to '${error?.message}' (retried: ${retryCount})`,
    error,
  )
}

export const sendCustomEventToAnalytics = (category: string, name: string, data: SnowPlowData): void => {
  trackSelfDescribingEvent({
    event: {
      schema: SnowplowSchema.CustomAction,
      data: {
        action_category: category,
        action_name: name,
      },
    },
    context: [
      {
        schema: SnowplowSchema.Common,
        data,
      },
    ],
  })
}

export const sendCustomDataEventToAnalytics = (data: SnowPlowData): void => {
  trackSelfDescribingEvent({
    event: {
      schema: SnowplowSchema.CustomAction,
      data,
    },
  })
}

export interface AnalyticsContextValue<T_SERVICE extends AnalyticsService<string, string, CommonAnalyticsModalName>> {
  analyticsService: T_SERVICE
}

export const resolveAnalyticsPage = (page: string): string => {
  let analyticsPage: string | CommonAnalyticsPageViewEvent

  switch (page) {
    case CommonStepType.Authenticate:
      analyticsPage = CommonAnalyticsPageViewEvent.Authenticate
      break
    case CommonStepType.Initialize:
      analyticsPage = CommonAnalyticsPageViewEvent.Initialize
      break
    case CommonStepType.GetUserInfo:
      analyticsPage = CommonAnalyticsPageViewEvent.GetUser
      break
    case CommonStepType.Send:
      analyticsPage = CommonAnalyticsPageViewEvent.Send
      break
    default:
      analyticsPage = page
      break
  }

  return analyticsPage
}
